/** Closed audience contracts. Evidence remains data, never model/tool instructions. */
export interface DevelopmentCopy {
  title: string;
  interpretation: string;
  addition: string;
  question: string;
  limit: string;
}
export interface DevelopmentInput extends DevelopmentCopy { privateNote: string }
export interface DevelopmentMoment {
  id: string;
  sourceType: 'transcript_upload' | 'post_meeting_reflection';
  sourceId: string;
  sourceRevision: string;
  quote: string;
  start: number;
  end: number;
  basis: 'observed' | 'reported';
  relation: 'supports' | 'contradicts';
  sourceDate: string | null;
  situationId: string | null;
  situationRevision: string | null;
}
export interface DevelopmentCoverage {
  asOf: string;
  windowStart: null;
  windowEnd: null;
  completeHistory: false;
  sourcesIncluded: number;
  verifiedSituations: number;
  unresolvedMoments: number;
}
export interface DevelopmentPerspective {
  choice: 'fits' | 'different' | 'question';
  note: string;
  revision: number;
  coachSeenRevision: number;
  updatedAt: string;
}
export interface DevelopmentPublication {
  contract: 'coach-development-shared-v1';
  id: string;
  connectionId: string;
  leaderId: string;
  coachId: string;
  relationshipRevision: string;
  revision: number;
  sharedAt: string;
  openedAt: string | null;
  canRespond: boolean;
  copy: DevelopmentCopy;
  evidence: DevelopmentMoment[];
  coverage: DevelopmentCoverage;
  perspective: DevelopmentPerspective | null;
  needsReview: boolean;
}
export interface DevelopmentCandidate {
  themeId: string;
  title: string;
  currentInterpretation: string | null;
  fingerprint: string;
  canShare: boolean;
  evidence: DevelopmentMoment[];
  coverage: DevelopmentCoverage;
}
export interface DevelopmentConnection {
  id: string;
  themeId: string;
  draftRevision: number;
  draftFingerprint: string | null;
  draft: DevelopmentInput | null;
  conflict: boolean;
  latestPublicationId: string | null;
  lastPublication: DevelopmentPublication | null;
  responses: (DevelopmentPerspective & { publicationId: string; publicationRevision: number })[];
  reviewHold: { revision: number; responseDigest: string; resolved: boolean; correctionProof: string | null } | null;
}
export interface DevelopmentUpdate {
  publicationId: string;
  publicationRevision: number;
  connectionId: string;
  leaderId: string;
  leaderName: string;
  relationshipRevision: string;
  revision: number;
  updatedAt: string;
}
export interface DevelopmentWorkspace {
  contract: 'coach-development-workspace-v1';
  leaderId: string;
  coachId: string;
  relationshipRevision: string;
  workRevision: string | null;
  canWrite: boolean;
  candidates: DevelopmentCandidate[];
  connections: DevelopmentConnection[];
}
export interface DevelopmentPreview {
  contract: 'coach-development-preview-v1';
  connectionId: string;
  leaderId: string;
  coachId: string;
  relationshipRevision: string;
  draftRevision: number;
  fingerprint: string;
  revision: number;
  previousPublicationId: string | null;
  copy: DevelopmentCopy;
  evidence: DevelopmentMoment[];
  coverage: DevelopmentCoverage;
}
export type DevelopmentScope = { leaderId: string; coachId: string };
export interface DevelopmentList extends DevelopmentScope {
  contract: 'coach-development-list-v1';
  relationshipRevision: string;
  entries: DevelopmentPublication[];
  newCount: number;
  newestPublicationId: string | null;
  nextOffset: number | null;
}

const invalid = (): never => { throw new Error('development_invalid'); };
export function developmentObject(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return invalid();
  return value as Record<string, unknown>;
}
function closed(value: unknown, keys: string[]): Record<string, unknown> {
  const result = developmentObject(value);
  if (Object.keys(result).length !== keys.length || !keys.every(key => Object.prototype.hasOwnProperty.call(result, key))) return invalid();
  return result;
}
function text(value: unknown, max: number, empty = false): string {
  if (typeof value !== 'string' || Array.from(value).length > max || (!empty && !value.trim())) return invalid();
  return value.trim();
}
export function developmentId(value: unknown): string {
  if (typeof value !== 'string' || !/^[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12}$/.test(value)) return invalid();
  return value;
}
function revision(value: unknown): string {
  if (typeof value !== 'string' || !/^[1-9][0-9]{0,18}$/.test(value) || BigInt(value) > 9223372036854775807n) return invalid();
  return value;
}
export function developmentFingerprint(value: unknown): string {
  if (typeof value !== 'string' || !/^[a-f0-9]{64}$/.test(value)) return invalid();
  return value;
}
function integer(value: unknown, min = 0, max = 2_147_483_647): number {
  if (!Number.isSafeInteger(value) || Number(value) < min || Number(value) > max) return invalid();
  return Number(value);
}
function bool(value: unknown): boolean { if (typeof value !== 'boolean') return invalid(); return value; }
function date(value: unknown): string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T/.test(value) || !Number.isFinite(Date.parse(value))) return invalid();
  return value;
}
/** Notification content is limited to current participant/version metadata. */
export function readDevelopmentUpdates(value: unknown): DevelopmentUpdate[] {
  if (!Array.isArray(value) || value.length > 50) return invalid();
  const updates = value.map(item => {
    const raw = closed(item, ['publicationId', 'publicationRevision', 'connectionId', 'leaderId', 'leaderName', 'relationshipRevision', 'revision', 'updatedAt']);
    return { publicationId: developmentId(raw.publicationId), publicationRevision: integer(raw.publicationRevision, 1),
      connectionId: developmentId(raw.connectionId), leaderId: developmentId(raw.leaderId), leaderName: text(raw.leaderName, 300),
      relationshipRevision: developmentId(raw.relationshipRevision), revision: integer(raw.revision, 1), updatedAt: date(raw.updatedAt) };
  });
  if (new Set(updates.map(item => item.publicationId)).size !== updates.length) return invalid();
  return updates;
}
function list<T>(value: unknown, read: (value: unknown) => T, max = 32): T[] {
  if (!Array.isArray(value) || value.length > max) return invalid();
  return value.map(read);
}
const copyKeys = ['title', 'interpretation', 'addition', 'question', 'limit'];
function copyFields(value: Record<string, unknown>): DevelopmentCopy {
  return { title: text(value.title, 140), interpretation: text(value.interpretation, 1600),
    addition: text(value.addition, 800), question: text(value.question, 600), limit: text(value.limit, 800) };
}
export function readDevelopmentInput(value: unknown): DevelopmentInput {
  const result = closed(value, [...copyKeys, 'privateNote']);
  return { ...copyFields(result), privateNote: text(result.privateNote, 2000, true) };
}
export function recipientDevelopmentCopy(input: DevelopmentInput): DevelopmentCopy {
  const { title, interpretation, addition, question, limit } = input;
  return { title, interpretation, addition, question, limit };
}
export function readDevelopmentMoment(value: unknown, recipient = true): DevelopmentMoment {
  const result = closed(value, ['id', 'sourceType', 'sourceId', 'sourceRevision', 'quote', 'start', 'end', 'basis', 'relation', 'sourceDate', 'situationId', 'situationRevision']);
  if (!['transcript_upload', 'post_meeting_reflection'].includes(String(result.sourceType))
    || recipient && result.sourceType !== 'transcript_upload'
    || !['observed', 'reported'].includes(String(result.basis))
    || !['supports', 'contradicts'].includes(String(result.relation))) return invalid();
  const quote = text(result.quote, 2000), start = integer(result.start, 0, 100000), end = integer(result.end, start + 1, 100000);
  // Trimming a quote would invalidate its source location. Reject instead.
  if (result.quote !== quote || Array.from(quote).length !== end - start) return invalid();
  if ((result.situationId === null) !== (result.situationRevision === null)) return invalid();
  return { id: developmentId(result.id), sourceType: result.sourceType as DevelopmentMoment['sourceType'],
    sourceId: developmentId(result.sourceId), sourceRevision: revision(result.sourceRevision), quote, start, end,
    basis: result.basis as DevelopmentMoment['basis'], relation: result.relation as DevelopmentMoment['relation'],
    sourceDate: result.sourceDate === null ? null : date(result.sourceDate),
    situationId: result.situationId === null ? null : developmentId(result.situationId),
    situationRevision: result.situationRevision === null ? null : revision(result.situationRevision) };
}
function readEvidence(value: unknown, recipient: boolean): DevelopmentMoment[] {
  const moments = list(value, item => readDevelopmentMoment(item, recipient));
  if (!moments.length || new Set(moments.map(moment => moment.id)).size !== moments.length) return invalid();
  const sourceStates = new Map<string, string>();
  const situations = new Map<string, string>();
  for (const moment of moments) {
    const sourceKey = `${moment.sourceType}:${moment.sourceId}`, sourceState = JSON.stringify([moment.sourceRevision, moment.sourceDate]);
    if (sourceStates.has(sourceKey) && sourceStates.get(sourceKey) !== sourceState) return invalid();
    sourceStates.set(sourceKey, sourceState);
    if (moment.situationId) {
      if (situations.has(moment.situationId) && situations.get(moment.situationId) !== moment.situationRevision) return invalid();
      situations.set(moment.situationId, moment.situationRevision!);
    }
  }
  return moments;
}
function readCoverage(value: unknown, evidence: DevelopmentMoment[]): DevelopmentCoverage {
  const result = closed(value, ['asOf', 'windowStart', 'windowEnd', 'completeHistory', 'sourcesIncluded', 'verifiedSituations', 'unresolvedMoments']);
  if (result.windowStart !== null || result.windowEnd !== null || result.completeHistory !== false) return invalid();
  const sourcesIncluded = new Set(evidence.map(moment => `${moment.sourceType}:${moment.sourceId}`)).size;
  const verifiedSituations = new Set(evidence.map(moment => moment.situationId).filter(Boolean)).size;
  const unresolvedMoments = evidence.filter(moment => moment.situationId === null).length;
  if (result.sourcesIncluded !== sourcesIncluded || result.verifiedSituations !== verifiedSituations || result.unresolvedMoments !== unresolvedMoments) return invalid();
  return { asOf: date(result.asOf), windowStart: null, windowEnd: null, completeHistory: false, sourcesIncluded, verifiedSituations, unresolvedMoments };
}
export function readDevelopmentPerspective(value: unknown): DevelopmentPerspective {
  const result = closed(value, ['choice', 'note', 'revision', 'coachSeenRevision', 'updatedAt']);
  if (!['fits', 'different', 'question'].includes(String(result.choice))) return invalid();
  const current = integer(result.revision, 1);
  return { choice: result.choice as DevelopmentPerspective['choice'], note: text(result.note, 2000, true), revision: current,
    coachSeenRevision: integer(result.coachSeenRevision, 0, current), updatedAt: date(result.updatedAt) };
}
export function readDevelopmentCandidate(value: unknown): DevelopmentCandidate {
  const result = closed(value, ['themeId', 'title', 'currentInterpretation', 'fingerprint', 'canShare', 'evidence', 'coverage']);
  const evidence = readEvidence(result.evidence, false);
  return { themeId: developmentId(result.themeId), title: text(result.title, 140), currentInterpretation: result.currentInterpretation === null ? null : text(result.currentInterpretation, 2000), fingerprint: developmentFingerprint(result.fingerprint),
    canShare: bool(result.canShare), evidence, coverage: readCoverage(result.coverage, evidence) };
}
export function readDevelopmentPublication(value: unknown, scope: DevelopmentScope): DevelopmentPublication {
  const result = closed(value, ['contract', 'id', 'connectionId', 'leaderId', 'coachId', 'relationshipRevision', 'revision', 'sharedAt', 'openedAt', 'canRespond', 'copy', 'evidence', 'coverage', 'perspective', 'needsReview']);
  if (result.contract !== 'coach-development-shared-v1' || result.leaderId !== scope.leaderId || result.coachId !== scope.coachId) return invalid();
  const evidence = readEvidence(result.evidence, true);
  const perspective = result.perspective === null ? null : readDevelopmentPerspective(result.perspective);
  const needsReview = bool(result.needsReview);
  if (perspective?.choice === 'different' && !needsReview) return invalid();
  return { contract: 'coach-development-shared-v1', id: developmentId(result.id), connectionId: developmentId(result.connectionId),
    leaderId: developmentId(result.leaderId), coachId: developmentId(result.coachId), relationshipRevision: developmentId(result.relationshipRevision),
    revision: integer(result.revision, 1), sharedAt: date(result.sharedAt), openedAt: result.openedAt === null ? null : date(result.openedAt),
    canRespond: bool(result.canRespond), copy: copyFields(closed(result.copy, copyKeys)), evidence, coverage: readCoverage(result.coverage, evidence), perspective, needsReview };
}
export function readDevelopmentPreview(value: unknown, scope: DevelopmentScope): DevelopmentPreview {
  const result = closed(value, ['contract', 'connectionId', 'leaderId', 'coachId', 'relationshipRevision', 'draftRevision', 'fingerprint', 'revision', 'previousPublicationId', 'copy', 'evidence', 'coverage']);
  if (result.contract !== 'coach-development-preview-v1' || result.leaderId !== scope.leaderId || result.coachId !== scope.coachId) return invalid();
  const evidence = readEvidence(result.evidence, true);
  return { contract: 'coach-development-preview-v1', connectionId: developmentId(result.connectionId), leaderId: developmentId(result.leaderId),
    coachId: developmentId(result.coachId), relationshipRevision: developmentId(result.relationshipRevision), draftRevision: integer(result.draftRevision, 1),
    fingerprint: developmentFingerprint(result.fingerprint), revision: integer(result.revision, 1), previousPublicationId: result.previousPublicationId === null ? null : developmentId(result.previousPublicationId), copy: copyFields(closed(result.copy, copyKeys)), evidence,
    coverage: readCoverage(result.coverage, evidence) };
}
export function readDevelopmentWorkspace(value: unknown, scope: DevelopmentScope): DevelopmentWorkspace {
  const result = closed(value, ['contract', 'leaderId', 'coachId', 'relationshipRevision', 'workRevision', 'canWrite', 'candidates', 'connections']);
  if (result.contract !== 'coach-development-workspace-v1' || result.leaderId !== scope.leaderId || result.coachId !== scope.coachId) return invalid();
  const candidates = list(result.candidates, readDevelopmentCandidate, 50);
  if (new Set(candidates.map(candidate => candidate.themeId)).size !== candidates.length) return invalid();
  const connections = list(result.connections, item => {
    const row = closed(item, ['id', 'themeId', 'draftRevision', 'draftFingerprint', 'draft', 'conflict', 'latestPublicationId', 'lastPublication', 'responses', 'reviewHold']);
    const conflict = bool(row.conflict);
    if (conflict && row.draft !== null) return invalid();
    return { id: developmentId(row.id), themeId: developmentId(row.themeId), draftRevision: integer(row.draftRevision),
      draftFingerprint: row.draftFingerprint === null ? null : developmentFingerprint(row.draftFingerprint),
      draft: row.draft === null ? null : readDevelopmentInput(row.draft), conflict,
      latestPublicationId: row.latestPublicationId === null ? null : developmentId(row.latestPublicationId),
      lastPublication: row.lastPublication === null ? null : readDevelopmentPublication(row.lastPublication, scope),
      reviewHold: row.reviewHold === null ? null : (() => {
        const hold = closed(row.reviewHold, ['revision', 'responseDigest', 'resolved', 'correctionProof']);
        return { revision: integer(hold.revision, 1), responseDigest: developmentFingerprint(hold.responseDigest), resolved: bool(hold.resolved),
          correctionProof: hold.correctionProof === null ? null : developmentFingerprint(hold.correctionProof) };
      })(),
      responses: list(row.responses, response => {
        const entry = closed(response, ['publicationId', 'publicationRevision', 'choice', 'note', 'revision', 'coachSeenRevision', 'updatedAt']);
        const { publicationId, publicationRevision, ...perspective } = entry;
        return { ...readDevelopmentPerspective(perspective), publicationId: developmentId(publicationId), publicationRevision: integer(publicationRevision, 1) };
      }, 100) };
  }, 50);
  if (new Set(connections.map(connection => connection.id)).size !== connections.length
    || new Set(connections.map(connection => connection.themeId)).size !== connections.length) return invalid();
  for (const connection of connections) {
    if (connection.lastPublication && (connection.lastPublication.connectionId !== connection.id
      || connection.lastPublication.relationshipRevision !== result.relationshipRevision
      || connection.lastPublication.id !== connection.latestPublicationId)) return invalid();
    if (connection.draft && (connection.draftRevision < 1 || connection.draftFingerprint === null)) return invalid();
    if (new Set(connection.responses.map(response => response.publicationId)).size !== connection.responses.length) return invalid();
  }
  return { contract: 'coach-development-workspace-v1', leaderId: developmentId(result.leaderId), coachId: developmentId(result.coachId),
    relationshipRevision: developmentId(result.relationshipRevision), workRevision: result.workRevision === null ? null : date(result.workRevision),
    canWrite: bool(result.canWrite), candidates, connections };
}
export function readDevelopmentList(value: unknown, scope: DevelopmentScope): DevelopmentList {
  const result = closed(value, ['contract', 'leaderId', 'coachId', 'relationshipRevision', 'entries', 'newCount', 'newestPublicationId', 'nextOffset']);
  if (result.contract !== 'coach-development-list-v1' || result.leaderId !== scope.leaderId || result.coachId !== scope.coachId) return invalid();
  const relationshipRevision = developmentId(result.relationshipRevision);
  const entries = list(result.entries, item => readDevelopmentPublication(item, scope), 20);
  if (entries.some(entry => entry.relationshipRevision !== relationshipRevision) || new Set(entries.map(entry => entry.connectionId)).size !== entries.length) return invalid();
  return { contract: 'coach-development-list-v1', ...scope, relationshipRevision, entries, newCount: integer(result.newCount),
    newestPublicationId: result.newestPublicationId === null ? null : developmentId(result.newestPublicationId),
    nextOffset: result.nextOffset === null ? null : integer(result.nextOffset, 1, 1000) };
}
